import { memo } from "react";
import { useTrackToggle } from "@livekit/components-react";
import { Track } from "livekit-client";
import { useIsUserSpeaking } from "@/hooks/use-is-user-speaking";
import { useGlobalState } from "@/hooks/useGlobalState";
import { getBrowserMicPermission } from "@/utils/get-browser-mic-permission";
import commentIcon from "/images/comment-icon.svg";
import shoppingBasket from "/images/shoping-basket.svg";
import microphone from "/images/microphone.svg";

interface VoiceChatFooterProps {
  onShoppingClick?: () => void;
  cartItems: number;
}

const MicSilent = () => (
  <div className="widget-content cursor-pointer flex items-center justify-center gap-[5px]">
    <span className="w-[2.8px] h-[2.8px]"></span>
    <span className="w-[2.8px] h-[2.8px]"></span>
    <span className="w-[2.8px] h-[2.8px]"></span>
    <span className="w-[2.8px] h-[2.8px]"></span>
    <span className="w-[2.8px] h-[2.8px]"></span>
    <span className="w-[2.8px] h-[2.8px]"></span>
    <span className="w-[2.8px] h-[2.8px]"></span>
  </div>
);

const MicSpeaking = () => (
  <div
    className={`widget-content bg-white rounded-[50%] flex items-center justify-center gap-[5px]`}
  >
    <span className="w-[2.8px] h-[4.7px]"></span>
    <span className="w-[2.8px] h-[23.3px]"></span>
    <span className="w-[2.8px] h-[42px]"></span>
    <span className="w-[2.8px] h-[28px]"></span>
    <span className="w-[2.8px] h-[14px]"></span>
    <span className="w-[2.8px] h-[23.3px]"></span>
    <span className="w-[2.8px] h-[4.7px]"></span>
  </div>
);

export const ChatFooterVoice = memo(function VoiceChatFooter({
  onShoppingClick,
  cartItems,
}: VoiceChatFooterProps) {
  const {
    setChatMode,
    isMicrophoneEnabled,
    setIsMicrophoneEnabled,
    setMicPermission,
  } = useGlobalState();
  const { enabled } = useTrackToggle({
    source: Track.Source.Microphone,
  });
  const isUserSpeaking = useIsUserSpeaking();

  return (
    <div className="voice-chat-footer w-[100%] flex items-center justify-between pt-[10px]">
      <button
        className="w-[68px] h-[68px] cursor-pointer flex items-center justify-center rounded-[50%] border-2 border-[#D8D8D8]"
        onClick={() => {
          setChatMode("text");
        }}
      >
        <img
          src={commentIcon}
          alt="comment-icon"
          className="w-[23.3px] h-[22.17]"
        />
      </button>

      {isUserSpeaking && enabled ? <MicSpeaking /> : <MicSilent />}

      <button
        className="w-[68px] h-[68px] cursor-pointer flex items-center justify-center rounded-[50%] border-2 border-[#D8D8D8] relative"
        onClick={onShoppingClick}
      >
        {cartItems > 0 && (
          <div className="absolute -top-1 -right-1 gradient-wrap text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
            {cartItems}
          </div>
        )}
        <img
          src={shoppingBasket}
          title="Open basket"
          alt="shopping-basket"
          className="w-[23.3px] h-[22.17]"
        />
      </button>
      <button
        className="w-[68px] h-[68px] cursor-pointer flex items-center justify-center rounded-[50%] border-2 border-[#D8D8D8]"
        onClick={async () => {
          setIsMicrophoneEnabled(!isMicrophoneEnabled);

          setMicPermission(await getBrowserMicPermission());
        }}
      >
        {!enabled ? (
          <div className="relative w-[28px] h-[28px] opacity-40">
            <img
              src={microphone}
              title="Enable microphone"
              alt="Microphone disabled"
              className="w-full h-full opacity-80 cursor-pointer"
            />
            <div className="absolute top-0 text-white left-0 w-[140%] h-[2px] bg-black transform -translate-y-1/2 rotate-45 origin-left">
              .
            </div>
          </div>
        ) : (
          <div className="relative w-[28px] h-[28px]">
            <img
              src={microphone}
              alt="Microphone enabled"
              title="Disable microphone"
              className="w-full h-full cursor-pointer"
            />
          </div>
        )}
      </button>
    </div>
  );
});
